import { $activeRoute, routePathNameChanged } from "@features/app/model";
import { getCurrencyByCountryCode, getDevicePlatformForPayment } from "@features/helpers";
import {
    PaymentApi,
    UserApi,
    StudentApi,
    IAppleRestorePurchase,
    TruegateApi
} from "@features/shared/api/course-data.api";
import type { TruegateStartAnonRequest } from "@features/shared/api/truegate.api";
import { contentLocaleChanged } from "@features/shared/locale";
import { persistMetaPredictionPlanCode } from "@features/shared/meta-events";
import { $student } from "@features/student/auth/model";

import {
    IPaymentPlan,
    SolidFormResponse,
    SolidgatePlan,
    EcommpayPaymentObject,
    ITruegatePlan
} from "@features/shared/entities/payment";
import { checkoutCloseClicked, checkoutPopupShown } from "@features/shared/events";

import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { PriceBreakdown } from "@solidgate/client-sdk-loader";

import { IPaymentTransaction } from "@features/shared/api/course-data.api";
import { connectWalletLogic, $walletActiveForm, $walletIsUpdating, $lastWalletRequest } from "./walletLogic";
import { isUpsellPurchaseRoute } from "./solidgatePlanCurrency";
import { getAdConsent } from "@utils/cookieConsent";

const isNotNull = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;
const getMiddleItem = <T>(items: T[]): T => items[Math.floor(items.length / 2)];

export const getEcommpayPlans = createEvent();
export const getEcommpayAnonPlans = createEvent();
export const getSolidgatePlans = createEvent();
export const getSolidgatePlansByIds = createEvent<string | string[]>();
export const getApplePlans = createEvent();
export const getTruegatePlans = createEvent();
export const startTruegatePayment = createEvent<TruegateStartAnonRequest>();

export const setPaymentPlanTags = createEvent<string[]>();
export const setPaymentSystemFlag = createEvent<"solidgate" | "ecommpay" | null>();
const getEcommpayPlansAuth = createEvent();

const setPlan = createEvent<IPaymentPlan>();
const setSolidgatePlan = createEvent<SolidgatePlan>();
const setApplePlan = createEvent<IPaymentPlan>();
const setTruegatePlan = createEvent<ITruegatePlan>();
const setCheckoutOldPrice = createEvent<number | null>();

setPlan.watch((plan) => {
    if (!plan) return;
    persistMetaPredictionPlanCode(plan.code || plan.id);
});

setSolidgatePlan.watch((plan) => {
    if (!plan) return;
    persistMetaPredictionPlanCode(plan.id);
});

setApplePlan.watch((plan) => {
    if (!plan) return;
    persistMetaPredictionPlanCode(plan.id);
});

const inAppPaymentFormVisibilityChanged = createEvent<boolean>();

const setCheckoutPopup = createEvent<boolean>();
const resetEcommpayPaymentObject = createEvent();
const setIsPaymentErrorPopupOpen = createEvent<boolean>();
const setPaymentErrorMessage = createEvent<string | null>();
const setPriceBreakdown = createEvent<PriceBreakdown | null>();

const setSpecialOfferPopup = createEvent<boolean>();
const nextSpecialOfferStep = createEvent();
export const getSpecialOffer = createEvent();

export const resetSolidgateResponse = createEvent();

export const prefetchSolidgateWalletForm = createEvent();
export const resetSolidgateWalletResponse = createEvent();

const setSolidgatePlanIndex = createEvent<number>();

const $paymentPlanTags = createStore<string[]>([]);
const $ecommpayPlans = createStore<IPaymentPlan[]>([]);
const $paymentPlansByTags = createStore<IPaymentPlan[]>([]);
const $currentPlanByTag = createStore<IPaymentPlan | null>(null).reset(routePathNameChanged);
const $ecommpayPaymentObject = createStore<EcommpayPaymentObject | null>(null).reset([
    routePathNameChanged,
    resetEcommpayPaymentObject
]);
export const $paymentSystemFlag = createStore<"solidgate" | "ecommpay" | null>(null).on(
    setPaymentSystemFlag,
    (_, value) => value
);
const $solidgatePlans = createStore<SolidgatePlan[]>([]);
const $filteredSolidgatePlans = createStore<SolidgatePlan[]>([]);
const $currentSolidgatePlan = createStore<SolidgatePlan | null>(null).reset(routePathNameChanged);
const $applePlans = createStore<IPaymentPlan[]>([]);
const $currentApplePlan = createStore<IPaymentPlan | null>(null).reset(routePathNameChanged);
const $truegatePlans = createStore<ITruegatePlan[]>([]);
const $filteredTruegatePlans = createStore<ITruegatePlan[]>([]);
// Renew fetches its own plan sets (both providers, one request each) instead
// of reading the shared catalog stores. This is false while that fetch is in
// flight and true once it has finished, success or failure — the renew paywall
// holds its skeleton on it rather than on a timer.
const setRenewPlansSettled = createEvent<boolean>();
const $renewPlansSettled = createStore(false).on(setRenewPlansSettled, (_, settled) => settled);
const $truegateTransactionId = createStore<string | null>(null).reset(routePathNameChanged);
const $truegateLoading = createStore(false);
const $currentTruegatePlan = createStore<ITruegatePlan | null>(null).reset(routePathNameChanged);
const $checkoutOldPrice = createStore<number | null>(null).on(setCheckoutOldPrice, (_, price) => price);

const $isCheckoutPopupOpen = createStore(false).on(setCheckoutPopup, (_, p) => p);
const $isSpecialOfferOpen = createStore(false).on(setSpecialOfferPopup, (_, p) => p);
const $specialOfferStep = createStore(0).on(nextSpecialOfferStep, (s) => s + 1);
const $isSpecialOfferReceived = createStore(false).on(getSpecialOffer, () => true);
const $isPaymentErrorPopupOpen = createStore(false).on(setIsPaymentErrorPopupOpen, (_, p) => p);
const $paymentErrorMessage = createStore<string | null>(null).on(setPaymentErrorMessage, (_, msg) => msg);
const $priceBreakdown = createStore<PriceBreakdown | null>(null)
    .on(setPriceBreakdown, (_, payload) => payload)
    .reset([routePathNameChanged, resetSolidgateResponse]);

export const setFirstPaywallTimerEnd = createEvent<number>();
export const setSecondPaywallTimerEnd = createEvent<number>();

export const $firstPaywallTimerEnd = createStore<number | null>(null).on(setFirstPaywallTimerEnd, (_, v) => v);
export const $secondPaywallTimerEnd = createStore<number | null>(null).on(setSecondPaywallTimerEnd, (_, v) => v);

const getUserLocation = createEvent();
const $solidgatePlanPriceIndex = createStore(0).on(setSolidgatePlanIndex, (_, p) => p);

const $solidFormResponse = createStore<SolidFormResponse | null>(null).reset(resetSolidgateResponse);

const $solidWalletFormResponse = createStore<SolidFormResponse | null>(null).reset([
    routePathNameChanged,
    resetSolidgateWalletResponse
]);

const $inAppPaymentFormVisible = createStore<boolean>(false).on(inAppPaymentFormVisibilityChanged, (_, p) => p);
const $currency = createStore("USD");
const $userCountry = createStore<string>("");

export const getPaymentHistoryFx = createEffect(async () => {
    return await PaymentApi.getPaymentHistory();
});

export const loadPaymentHistoryOnce = createEvent();
export const reloadPaymentHistory = createEvent();

const $isPaymentHistoryLoaded = createStore(false)
    .on(getPaymentHistoryFx.doneData, () => true)
    .reset(loadPaymentHistoryOnce);

export const $paymentHistoryError = createStore<Error | null>(null);

export const $isPaymentHistoryEmpty = createStore(false).on(
    getPaymentHistoryFx.doneData,
    (_, res) => !res.transactions || res.transactions.length === 0
);

export const $paymentHistory = createStore<IPaymentTransaction[]>([]).on(
    getPaymentHistoryFx.doneData,
    (_, res) => res.transactions ?? []
);

const getEcommpayPlansFx = createEffect(() => {
    if (typeof window !== "undefined") {
        const existingUuid = localStorage.getItem("user_uuid");
        return PaymentApi.getEcommpayPlans(existingUuid!);
    }
});

const getEcommpayAnonPlansFx = createEffect(() => {
    if (typeof window !== "undefined") {
        return PaymentApi.getEcommpayAnonPlans("123");
    }
});

const getEcommpayPlansAuthFx = createEffect(() => {
    if (typeof window !== "undefined") {
        return PaymentApi.getEcommpayPlansAuth();
    }
});

const getEcommpayPaymentObjectFx = createEffect(async (plan: IPaymentPlan) => {
    if (typeof window === "undefined") return null;

    const deviceId = window.localStorage.getItem("user_uuid");
    if (!deviceId) return null;

    const numericId = Number.parseInt(plan.id, 10);
    if (Number.isNaN(numericId)) return null;

    const platform = getDevicePlatformForPayment();

    return await PaymentApi.getEcommpayPaymentObject(numericId, deviceId, platform);
});

$ecommpayPaymentObject.on(getEcommpayPaymentObjectFx.doneData, (_, data) => data ?? null);

const getSolidgatePlansFx = createEffect(async () => {
    if (typeof window !== "undefined") {
        const response = await PaymentApi.getSolidgatePlans();
        return response.map((p) => new SolidgatePlan().restore(p));
    }
});

const getApplePlansFx = createEffect(async () => {
    if (typeof window !== "undefined") {
        return await PaymentApi.getApplePlans();
    }
});

const getTruegatePlansFx = createEffect(async () => {
    if (typeof window !== "undefined") {
        return await PaymentApi.getTruegatePlans();
    }
});

const getTruegatePlansWithLocationFx = createEffect(async () => {
    const location = await UserApi.getUserLocation();
    const country = location?.country ?? "";
    const currency = country === "" ? "USD" : getCurrencyByCountryCode(country);

    const plansResponse = typeof window !== "undefined" ? await TruegateApi.getTruegatePlans() : [];
    const plans = plansResponse ?? [];

    return { currency, country, plans };
});

const startTruegatePaymentFx = createEffect(async (params: TruegateStartAnonRequest) => {
    return await TruegateApi.startAnonPayment({ ...params, metadata: { ...params.metadata, ...getAdConsent() } });
});

// Fetch user location first to determine currency, then load Solidgate plans
const getSolidgatePlansWithLocationFx = createEffect(async () => {
    const location = await UserApi.getUserLocation();
    const country = location?.country ?? "";
    const currency = country === "" ? "USD" : getCurrencyByCountryCode(country);

    const plansResponse = typeof window !== "undefined" ? await PaymentApi.getSolidgatePlans() : [];
    const plans = (plansResponse ?? []).map((p) => new SolidgatePlan().restore(p));

    return { currency, plans } as { currency: string; plans: SolidgatePlan[] };
});

const getSolidgatePlansByIdsFx = createEffect(async (ids: string | string[]) => {
    if (typeof window === "undefined") return { currency: "USD", plans: [] as SolidgatePlan[], country: "" };

    const location = await UserApi.getUserLocation().catch(() => null);
    const country = location?.country ?? "";
    const currency = country === "" ? "USD" : getCurrencyByCountryCode(country);

    const plansResponse = await PaymentApi.getSolidgatePlans(ids);
    const plans = (plansResponse ?? []).map((p) => new SolidgatePlan().restore(p));

    return { currency, plans, country } as { currency: string; plans: SolidgatePlan[]; country: string };
});

const getSolidgateFormFx = createEffect(async ({ currency, planId }: { currency: string; planId?: string }) => {
    if (typeof window === "undefined" || !planId) return null;

    const testPlanId = localStorage.getItem("sg_test_plan_id");
    const testCurrency = localStorage.getItem("sg_test_currency");
    if (testPlanId && testCurrency) {
        planId = testPlanId;
        currency = testCurrency;
    }

    const student = $student.getState();
    const onboardingId = sessionStorage.getItem("onboarding_id");

    if (student && student.id) {
        const userId = String(student.id);
        const urlParams = new URLSearchParams(window.location.search);

        const storedParamsString = sessionStorage.getItem("query_params") ?? "";
        const storedParams = new URLSearchParams(storedParamsString);
        const utmParams = Object.fromEntries(
            Array.from(storedParams.entries()).filter(([key]) => key.startsWith("utm_"))
        ) as Record<string, string>;

        const metadata = {
            onboardingId,
            website: window.location.host + window.location.pathname,
            trafficSource: urlParams.get("referring_domain") ?? undefined,
            deviceId: urlParams.get("dev_id") ?? localStorage.getItem("user_uuid") ?? undefined,
            ...getAdConsent(),
            ...utmParams
        };
        return PaymentApi.getSolidgateFormByUserId(userId, planId, currency, metadata);
    }

    const deviceOrUuid = localStorage.getItem("user_uuid");

    if (deviceOrUuid) {
        return PaymentApi.getSolidgateForm(deviceOrUuid, planId, currency, getAdConsent());
    }

    return null;
});

const getUserLocationFx = createEffect(async () => {
    const response = await UserApi.getUserLocation();

    return response.country;
});

const trackCheckoutPopupCloseFx = createEffect(() => {
    let id: string | null = "";
    if (typeof window !== "undefined") {
        id = sessionStorage.getItem("onboarding_id");
    }
    checkoutCloseClicked(id ? id : "");
});

const trackCheckoutPopupShownFx = createEffect((step: number) => {
    let id: string | null = "";
    if (typeof window !== "undefined") {
        id = sessionStorage.getItem("onboarding_id");
    }
    checkoutPopupShown(id ? id : "", step);
});

sample({
    clock: getPaymentHistoryFx.failData,
    target: $paymentHistoryError
});

sample({
    clock: getPaymentHistoryFx.done,
    fn: () => null,
    target: $paymentHistoryError
});

sample({
    clock: loadPaymentHistoryOnce,
    source: $paymentHistory,
    filter: (history) => history.length === 0,
    target: getPaymentHistoryFx
});

sample({
    clock: reloadPaymentHistory,
    target: getPaymentHistoryFx
});

sample({
    clock: getEcommpayPlans,
    target: getEcommpayPlansFx
});

sample({
    clock: getEcommpayAnonPlans,
    target: getEcommpayAnonPlansFx
});

sample({
    clock: getEcommpayPlansAuth,
    target: getEcommpayPlansAuthFx
});

sample({
    clock: getEcommpayAnonPlansFx.doneData,
    fn: (plans) => plans ?? [],
    target: $ecommpayPlans
});

sample({
    clock: getEcommpayPlansFx.doneData,
    fn: (plans) => plans ?? [],
    target: $ecommpayPlans
});

sample({
    clock: [$paymentPlansByTags, routePathNameChanged],
    source: {
        plans: $paymentPlansByTags,
        current: $currentPlanByTag
    },
    filter: ({ plans, current }) => {
        if (typeof window === "undefined") return false;
        const path = window.location.pathname;
        if (path.includes("/upsell")) return false;
        if (path.includes("/profile/settings/subscriptions/renew")) return false;
        if (plans.length === 0) return false;
        if (!current) return true;
        return !plans.some((plan) => plan.id === current.id);
    },
    fn: ({ plans }) => getMiddleItem(plans),
    target: $currentPlanByTag
});

sample({
    clock: setPlan,
    target: $currentPlanByTag
});

sample({
    clock: $currentPlanByTag,
    filter: isNotNull,
    target: getEcommpayPaymentObjectFx
});

sample({
    clock: setCheckoutPopup,
    filter: (isOpen) => !isOpen,
    target: resetEcommpayPaymentObject
});

sample({
    clock: setCheckoutPopup,
    source: $currentPlanByTag,
    filter: (plan, isOpen) => isOpen && !!plan,
    fn: (plan) => plan as IPaymentPlan,
    target: getEcommpayPaymentObjectFx
});

sample({
    source: $ecommpayPlans,
    clock: inAppPaymentFormVisibilityChanged,
    filter: (s) => s.length == 0,
    target: getEcommpayPlans
});

sample({
    clock: getEcommpayPlansAuthFx.doneData,
    fn: (plans) => plans ?? [],
    target: $ecommpayPlans
});

sample({
    clock: getTruegatePlans,
    target: getTruegatePlansWithLocationFx
});

sample({
    clock: getTruegatePlansWithLocationFx.doneData,
    fn: ({ plans }) => plans ?? [],
    target: $truegatePlans
});

sample({
    clock: getTruegatePlansWithLocationFx.doneData,
    fn: ({ currency }) => currency,
    target: $currency
});

sample({
    clock: getTruegatePlansWithLocationFx.doneData,
    fn: ({ country }) => country,
    target: $userCountry
});

sample({
    clock: startTruegatePayment,
    fn: () => true,
    target: $truegateLoading
});

sample({
    clock: startTruegatePayment,
    target: startTruegatePaymentFx
});

sample({
    clock: startTruegatePaymentFx.doneData,
    fn: (data) => data.transactionId,
    target: $truegateTransactionId
});

sample({
    clock: startTruegatePaymentFx.finally,
    fn: () => false,
    target: $truegateLoading
});

sample({
    clock: setTruegatePlan,
    target: $currentTruegatePlan
});

sample({
    clock: setPaymentPlanTags,
    target: $paymentPlanTags
});

sample({
    source: { tags: $paymentPlanTags, plans: $ecommpayPlans, currency: $currency },
    filter: () => typeof window !== "undefined",
    fn: ({ tags, plans, currency }) => {
        if (tags.length === 0) {
            return [];
        }

        const matchedPlans = plans.filter(
            (plan) => tags.every((tag) => plan.tags.includes(tag)) && plan.tags.every((tag) => tags.includes(tag))
        );

        const sortedPlans = matchedPlans.sort((a, b) => a.newPrice - b.newPrice);

        const isChallengeV7OrV8Ecommpay = tags.some((tag) =>
            [
                "challenge_100k_v7_ecommpay",
                "challenge_100k_v7_ecommpay_discount",
                "challenge_100k_v8_e",
                "challenge_100k_v8_e_discount"
            ].includes(tag)
        );

        if (isChallengeV7OrV8Ecommpay && currency) {
            const byCurrency = sortedPlans.filter((plan) => plan.currency === currency);
            if (byCurrency.length > 0) {
                return byCurrency;
            }
        }

        return sortedPlans;
    },
    target: $paymentPlansByTags
});

sample({
    clock: getSolidgatePlans,
    target: getSolidgatePlansWithLocationFx
});

sample({
    clock: getSolidgatePlansByIds,
    target: getSolidgatePlansByIdsFx
});

sample({
    clock: getSolidgatePlansByIdsFx.doneData,
    fn: ({ plans }) => plans ?? [],
    target: $solidgatePlans
});

sample({
    clock: getSolidgatePlansByIdsFx.doneData,
    fn: ({ currency }) => currency,
    target: $currency
});

sample({
    clock: getSolidgatePlansByIdsFx.doneData,
    fn: ({ country }) => country,
    target: $userCountry
});

sample({
    clock: getApplePlans,
    target: getApplePlansFx
});

sample({
    clock: getSolidgatePlansWithLocationFx.doneData,
    fn: ({ plans }) => plans ?? [],
    target: $solidgatePlans
});

// --- plan names follow the language -----------------------------------------
// A plan's name is localized by the backend (plan_metadata) and the locale is
// resolved from the cookie when the request goes out, so a list fetched before
// the user switched the language keeps the previous language's names for as
// long as it sits in the store. Screens ask for plans once — the renew paywall
// only when the store is empty — so a language switch and a walk back to the
// paywall showed the old names until a full reload. Every other model holding
// localized content already refetches on contentLocaleChanged (courses,
// roadmaps, tips, upsell view data); the plan lists now do too, replaying
// whichever load filled them, arguments and all.

type SolidgatePlansLoader = "none" | "location" | "all" | "ids";

const $solidgatePlansLoader = createStore<SolidgatePlansLoader>("none")
    .on(getSolidgatePlansWithLocationFx, () => "location" as const)
    .on(getSolidgatePlansFx, () => "all" as const)
    .on(getSolidgatePlansByIdsFx, () => "ids" as const);

// The ids sit in their own store rather than inside the loader value: effector
// does not narrow a sample's `fn` from the type predicate in its `filter`, so
// reading them off a tagged union there is a type error.
const $lastSolidgatePlanIds = createStore<string | string[]>("").on(
    getSolidgatePlansByIdsFx,
    (_, ids) => ids
);

// Truegate takes no arguments, so the flag is only "has anything been loaded":
// a locale switch must not fetch plans for a screen that never asked for them.
const $truegatePlansLoaded = createStore(false).on(getTruegatePlansWithLocationFx, () => true);

sample({
    clock: contentLocaleChanged,
    source: $solidgatePlansLoader,
    filter: (loader) => loader === "location",
    fn: () => undefined,
    target: getSolidgatePlansWithLocationFx
});

sample({
    clock: contentLocaleChanged,
    source: $solidgatePlansLoader,
    filter: (loader) => loader === "all",
    fn: () => undefined,
    target: getSolidgatePlansFx
});

sample({
    clock: contentLocaleChanged,
    source: { loader: $solidgatePlansLoader, ids: $lastSolidgatePlanIds },
    filter: ({ loader }) => loader === "ids",
    fn: ({ ids }) => ids,
    target: getSolidgatePlansByIdsFx
});

sample({
    clock: contentLocaleChanged,
    source: $truegatePlansLoaded,
    filter: Boolean,
    fn: () => undefined,
    target: getTruegatePlansWithLocationFx
});

sample({
    clock: getApplePlansFx.doneData,
    fn: (plans) => plans ?? [],
    target: $applePlans
});

sample({
    clock: getSolidgatePlansWithLocationFx.doneData,
    fn: ({ currency }) => currency,
    target: $currency
});

sample({
    clock: setSolidgatePlan,
    target: $currentSolidgatePlan
});

sample({
    clock: setApplePlan,
    target: $currentApplePlan
});

// Connect wallet logic
connectWalletLogic({
    $currentSolidgatePlan,
    $solidgatePlanPriceIndex,
    $currency,
    $isCheckoutPopupOpen,
    getSolidgateFormFx
});

// For checkout popup - use old logic (without requestId)
sample({
    clock: $isCheckoutPopupOpen,
    source: { $currentSolidgatePlan, $isCheckoutPopupOpen, $currency, $solidgatePlanPriceIndex },
    filter: ({ $isCheckoutPopupOpen }) => $isCheckoutPopupOpen,
    fn: ({ $currentSolidgatePlan, $currency, $solidgatePlanPriceIndex }) => {
        const planId = $currentSolidgatePlan?.id;
        const prices = $currentSolidgatePlan?.prices ?? [];
        const price = prices[$solidgatePlanPriceIndex] ?? null;
        const currency = price?.currency ?? $currency;
        return { planId, currency };
    },
    target: getSolidgateFormFx
});

sample({
    clock: $solidgatePlanPriceIndex,
    source: { $currentSolidgatePlan, $isCheckoutPopupOpen, $currency, $solidgatePlanPriceIndex },
    filter: ({ $isCheckoutPopupOpen, $currentSolidgatePlan }) => $isCheckoutPopupOpen && !!$currentSolidgatePlan,
    fn: ({ $currentSolidgatePlan, $currency, $solidgatePlanPriceIndex }) => {
        const planId = $currentSolidgatePlan?.id;
        const prices = $currentSolidgatePlan?.prices ?? [];
        const price = prices[$solidgatePlanPriceIndex] ?? null;
        const currency = price?.currency ?? $currency;
        return { planId, currency };
    },
    target: getSolidgateFormFx
});

sample({
    clock: $currentSolidgatePlan,
    source: { $currentSolidgatePlan, $isCheckoutPopupOpen, $currency, $solidgatePlanPriceIndex },
    filter: ({ $isCheckoutPopupOpen, $currentSolidgatePlan }) => $isCheckoutPopupOpen && !!$currentSolidgatePlan,
    fn: ({ $currentSolidgatePlan, $currency, $solidgatePlanPriceIndex }) => {
        const planId = $currentSolidgatePlan?.id;
        const prices = $currentSolidgatePlan?.prices ?? [];
        const price = prices[$solidgatePlanPriceIndex] ?? null;
        const currency = price?.currency ?? $currency;
        return { planId, currency };
    },
    target: getSolidgateFormFx
});

sample({
    clock: prefetchSolidgateWalletForm,
    source: { plan: $currentSolidgatePlan, currency: $currency, idx: $solidgatePlanPriceIndex },
    filter: ({ plan }) => !!plan,
    fn: ({ plan, currency, idx }) => {
        const prices = plan!.prices ?? [];
        const price = prices[idx] ?? null;
        const cur = price?.currency ?? currency;
        return { planId: plan!.id, currency: cur };
    },
    target: getSolidgateFormFx
});

// Old logic for $solidWalletFormResponse (for backward compatibility)
// Update only for old requests (which are not processed by walletLogic)
// walletLogic processes its requests through wrapper and updates $walletActiveForm
sample({
    clock: getSolidgateFormFx.doneData,
    source: { isOpen: $isCheckoutPopupOpen, lastWalletRequest: $lastWalletRequest },
    filter: ({ isOpen, lastWalletRequest }) => !isOpen && lastWalletRequest === null, // Only old requests (not wallet)
    fn: (_, data) => data,
    target: $solidWalletFormResponse
});

sample({
    clock: getSolidgateFormFx.doneData,
    source: $isCheckoutPopupOpen,
    filter: (isOpen) => isOpen,
    fn: (_, data) => data,
    target: $solidFormResponse
});

sample({
    clock: getSolidgateFormFx.failData,
    fn: (data) => console.error(`Solidgate error: ${JSON.stringify(data)}`)
});

sample({
    source: $isCheckoutPopupOpen,
    filter: (isOpen) => isOpen === false,
    target: [resetSolidgateResponse, trackCheckoutPopupCloseFx]
});

sample({
    clock: [$filteredSolidgatePlans, routePathNameChanged],
    source: {
        plans: $filteredSolidgatePlans,
        current: $currentSolidgatePlan
    },
    filter: ({ plans, current }) => {
        if (plans.length === 0) return false;
        if (!current) return true;
        return !plans.some((plan) => plan.id === current.id);
    },
    fn: ({ plans }) => getMiddleItem(plans),
    target: $currentSolidgatePlan
});

sample({
    clock: $currentSolidgatePlan,
    source: $filteredSolidgatePlans,
    filter: (plans, current) => {
        if (typeof window === "undefined") return false;
        return (
            current === null &&
            plans.length > 0 &&
            window.location.pathname.includes("/profile/settings/subscriptions/renew")
        );
    },
    fn: (plans) => plans[Math.floor(plans.length / 2)],
    target: $currentSolidgatePlan
});

sample({
    clock: [$specialOfferStep, $isSpecialOfferOpen],
    source: { $specialOfferStep, $isSpecialOfferOpen },
    filter: ({ $specialOfferStep, $isSpecialOfferOpen }) => $specialOfferStep < 2 && $isSpecialOfferOpen,
    fn: ({ $specialOfferStep }) => $specialOfferStep,
    target: trackCheckoutPopupShownFx
});

sample({
    clock: getUserLocation,
    target: getUserLocationFx
});

sample({
    clock: getUserLocationFx.doneData,
    fn: (country) => {
        return country === "" ? "USD" : getCurrencyByCountryCode(country);
    },
    target: $currency
});

sample({
    clock: getUserLocationFx.doneData,
    target: $userCountry
});

// Upsells select their Solidgate price by subscription currency in UpsellModule.
// Geolocation is still loaded there for UPI eligibility, but must not replace that price.
sample({
    source: { currentSolidgatePlan: $currentSolidgatePlan, currency: $currency, activeRoute: $activeRoute },
    filter: ({ activeRoute }) => !isUpsellPurchaseRoute(activeRoute),
    fn: ({ currentSolidgatePlan, currency }) => {
        return Math.max(0, currentSolidgatePlan?.prices.findIndex((price) => price.currency === currency) ?? 0);
    },
    target: setSolidgatePlanIndex
});

sample({
    clock: getSolidgatePlansFx.doneData,
    fn: (plans) => plans ?? [],
    target: $solidgatePlans
});

const contract = new ModelContract(
    {
        $ecommpayPlans,
        $userCountry,
        $paymentPlansByTags,
        $currentPlanByTag,
        $ecommpayPaymentObject,
        $inAppPaymentFormVisible,
        $solidgatePlans,
        $currentSolidgatePlan,
        $applePlans,
        $currentApplePlan,
        $checkoutOldPrice,
        $solidFormResponse,
        $solidWalletFormResponse,
        $walletActiveForm,
        $walletIsUpdating,
        $isCheckoutPopupOpen,
        $isSpecialOfferOpen,
        $specialOfferStep,
        $filteredSolidgatePlans,
        $isSpecialOfferReceived,
        $solidgatePlanPriceIndex,
        $firstPaywallTimerEnd,
        $secondPaywallTimerEnd,
        $paymentHistory,
        $isPaymentHistoryEmpty,
        $paymentHistoryError,
        $isPaymentHistoryLoaded,
        $isPaymentErrorPopupOpen,
        $paymentErrorMessage,
        $paymentSystemFlag,
        $currency,
        $priceBreakdown,
        $truegatePlans,
        $filteredTruegatePlans,
        $renewPlansSettled,
        $truegateTransactionId,
        $truegateLoading,
        $currentTruegatePlan
    },
    {
        getEcommpayPlans,
        getSolidgatePlans,
        getSolidgatePlansByIds,
        getApplePlans,
        setPlan,
        setSolidgatePlan,
        setRenewPlansSettled,
        setApplePlan,
        setCheckoutOldPrice,
        inAppPaymentFormVisibilityChanged,
        setCheckoutPopup,
        setSpecialOfferPopup,
        nextSpecialOfferStep,
        getSpecialOffer,
        setPaymentPlanTags,
        getEcommpayAnonPlans,
        setSolidgatePlanIndex,
        getUserLocation,
        setFirstPaywallTimerEnd,
        setSecondPaywallTimerEnd,
        loadPaymentHistoryOnce,
        reloadPaymentHistory,
        setIsPaymentErrorPopupOpen,
        setPaymentErrorMessage,
        setPriceBreakdown,
        setPaymentSystemFlag,
        prefetchSolidgateWalletForm,
        resetSolidgateWalletResponse,
        getTruegatePlans,
        startTruegatePayment,
        setTruegatePlan
    },
    {
        getSolidgateFormFx,
        getApplePlansFx,
        getEcommpayPlansFx,
        getEcommpayAnonPlansFx,
        getSolidgatePlansFx,
        getSolidgatePlansByIdsFx,
        getUserLocationFx,
        getPaymentHistoryFx,
        getEcommpayPaymentObjectFx,
        getTruegatePlansFx,
        getTruegatePlansWithLocationFx,
        startTruegatePaymentFx
    },
    {}
);

export type IPaymentContract = typeof contract;

export class PaymentModule extends ModuleDescription<IPaymentContract> {
    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            source: $ecommpayPlans,
            clock: inAppPaymentFormVisibilityChanged,
            filter: (s, p) => s.length === 0 && p, //TODO: don't load ecommpay plans when solidgate is selected
            target: getEcommpayPlansAuth
        });
    }
}
